import * as React from 'react';

import { AccountDetail } from '@flipdish/api-client-typescript';
import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Theme,
  withStyles,
} from '@material-ui/core';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import AddAPhoto from '@material-ui/icons/AddAPhotoOutlined';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { Md5 } from 'ts-md5';

interface IMaterialUiProps {
  classes?: any;
}

interface IDispatchProps {
  dispatch: () => any;
}

interface IStoreStateProps {
  account: AccountDetail;
  width: Breakpoint;
  translate: (input: string) => string;
}

interface IProfileProps {
  open: boolean;
}

export type Props = IMaterialUiProps & IDispatchProps & IProfileProps & IStoreStateProps;

const downBreakpoint = 'sm';

const styles = (theme: Theme) =>
  createStyles({
    mainDiv: {
      width: '100%',
      paddingRight: 32,
      [theme.breakpoints.only('md')]: {
        paddingRight: 24,
      },
      [theme.breakpoints.only('sm')]: {
        paddingRight: 20,
      },
      [theme.breakpoints.only('xs')]: {
        paddingRight: 0,
      },
    },
    gravatarDiv: {
      position: 'relative',
      width: '100%',
      maxWidth: '223px',
      height: 'auto',
      borderRadius: '223px',
      '&:hover': {
        '& > div:nth-child(2)': {
          opacity: 0.6,
        },
        '& > div:nth-child(3)': {
          opacity: 1,
        },
      },
    },
    gravatarImage: {
      display: 'block',
      width: '100%',
      height: 'auto',
      borderRadius: '180px',
    },
    gravatarOverlay: {
      position: 'absolute',
      width: '100%',
      height: 'auto',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      borderRadius: '180px',
      opacity: 0,
      transition: '.5s ease',
      backgroundColor: '#b9d4fc',
      fontSize: '0px',
      [theme.breakpoints.down(downBreakpoint)]: {
        display: 'none',
      },
    },
    gravatarInsideOverlay: {
      position: 'absolute',
      margin: 'auto',
      width: '56px',
      height: '56px',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      borderRadius: '28px',
      boxShadow: '0 2px 4px 0',
      opacity: 0,
      backgroundColor: '#ffffff',
      alignSelf: 'center',
      [theme.breakpoints.down(downBreakpoint)]: {
        display: 'none',
      },
    },
  });

class ProfileGravatar extends React.Component<Props, { open: boolean }> {
  public constructor(props) {
    super(props);
    this.state = {
      open: false,
    };
  }

  public render() {
    const { translate, account, width, classes } = this.props;
    const avatarImageUrl = account.Email
      ? 'https://www.gravatar.com/avatar/' +
        Md5.hashStr(account.Email) +
        '?d=https%3A%2F%2Fflipdish.imgix.net%2Fdefault-avatar.jpg' +
        (isWidthDown(downBreakpoint, width, true) ? '' : '&s=200')
      : '';
    return (
      <div>
        <div className={classes.mainDiv}>
          <div
            className={classes.gravatarDiv}
            data-fd="btn-gravatar-change-open"
            onClick={this.gravatarClick(width)}
          >
            <img src={avatarImageUrl} className={classes.gravatarImage} />
            <div className={classes.gravatarOverlay} />
            <div className={classes.gravatarInsideOverlay}>
              <AddAPhoto style={{ margin: '16px' }} />
            </div>
          </div>
          <Dialog
            open={this.state.open}
            onClose={this.handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">{translate('Change_avatar')}</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {translate('Would_you_like_to_change_your_avatar_on_gravatar')}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={this.handleClose}
                color="primary"
                data-fd="btn-gravatar-change-cancel"
              >
                {translate('Cancel')}
              </Button>
              <Button
                onClick={this.handleChange}
                color="primary"
                autoFocus
                data-fd="btn-gravatar-change-agree"
              >
                {translate('Change')}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    );
  }

  private gravatarClick = (currentDown: Breakpoint) => () => {
    if (!isWidthDown(downBreakpoint, currentDown, true)) {
      this.setState({ open: true });
    }
  };

  private handleClose = () => {
    this.setState({ open: false });
  };

  private handleChange = (event: React.MouseEvent<HTMLElement>) => {
    const newLink = window.open('https://www.gravatar.com/emails', '_blank', 'noopener,noreferrer');
    this.setState({ open: false });
  };
}

function mapStateToProps(state) {
  const { account, locale } = state;
  return {
    account,
    translate: getTranslate(locale),
  };
}

const EnhancedComponent = compose(
  withStyles(styles, {
    name: 'ProfileGravatar',
  }),
  withWidth(),
  connect(mapStateToProps)
)(ProfileGravatar);

export { EnhancedComponent as ProfileGravatar };
